import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, unref as _unref, withCtx as _withCtx, createVNode as _createVNode } from "vue"

const _hoisted_1 = { class: "JournalingReport" }
const _hoisted_2 = {
  key: 0,
  class: "content-container"
}
const _hoisted_3 = /*#__PURE__*/_createElementVNode("div", { class: "loading" }, [
  /*#__PURE__*/_createElementVNode("span", { class: "loader" })
], -1)
const _hoisted_4 = [
  _hoisted_3
]
const _hoisted_5 = {
  key: 1,
  class: "content-container"
}
const _hoisted_6 = /*#__PURE__*/_createElementVNode("div", { class: "top-content" }, null, -1)
const _hoisted_7 = {
  key: 0,
  class: "highlight-green-bold"
}
const _hoisted_8 = {
  key: 1,
  class: "failure"
}
const _hoisted_9 = { key: 0 }
const _hoisted_10 = ["onClick", "disabled", "title"]
const _hoisted_11 = { key: 1 }
const _hoisted_12 = ["onClick", "disabled", "title"]

import { ref, onMounted, inject } from "vue";
import { useRoute } from "vue-router";
import router from "@/router";
import { apiLoggerKey } from "@/types/ServiceKeys";
import { JournalingReportService } from "@/services/helpers/JournalingReportHelper";
import { FunctionAccessLevels, DenyFunctionLevelAccess } from "@/config/AuthConfig";
import IJournalReportItem from "@/interfaces/Journaling/IJournalingReportItem";
import Table from "@/components/TablePersistentChecking.vue";
import log from "loglevel";
import { AxiosError, AxiosResponse } from "axios";
import { DisplayableKeys } from "@/services/SorterService";
import useNotifierWithErrFormatter from "@/services/composables/NotifierWithErrFormatter";

const fileName = "Journaling.vue";

export default /*#__PURE__*/_defineComponent({
  __name: 'JournalingReport',
  setup(__props) {

const Logger = inject(apiLoggerKey);
const infoError = ref();
const isLoading = ref(true);
const backoutButtonStatuses = ref<Record<string, boolean>>({});
const pdfButtonStatuses = ref<Record<string, boolean>>({});
const route = useRoute();
const routeParams = route.path.split("/").pop()!.split("-");
const jobId = routeParams[0];
const journalType = route.query.JournalType!.toString();
const jnlReportServices: JournalingReportService = new JournalingReportService();
const journalingReport = ref<IJournalReportItem[] | undefined>();
const displayedJournalingReport = ref<IJournalReportItem[] | undefined>();
const cols: DisplayableKeys<IJournalReportItem>[] = [
    "IssueDate",
    "Status",
    "ReportName",
    "JournalReference",
    "BackoutAvailable",
];
const labels = ["Issue Date", "Status", "Report Name", "Journal Reference", "Action"];

log.debug("Journaling Report");

onMounted(async () => {
    log.trace("onMounted() JournalingReport");

    document.title = 'Journaling (' + jobId + ') - UV';

    const name = onMounted.name;
    const Class = fileName + "/" + name;
    var Message = `Getting Journaling report (Job Id: ${jobId}, Journal type: ${journalType})`;

    isLoading.value = true;

    log.debug(`name()\n${Message}`);
    await jnlReportServices
        .GetJournalingReport(jobId, journalType)
        .then((data: IJournalReportItem[]) => {
            journalingReport.value = data;
            displayedJournalingReport.value = journalingReport.value.map(
                jnlReportServices.ParseItems
            );

            // Button Statuses
            ManagePdfDownloadUrlsAndButtonStatuses(data);

            isLoading.value = false;
            Logger?.LogInformation(Message, Class);
        })
        .catch((error: any) => {
            isLoading.value = false;

            if (error instanceof AxiosError) {
                useNotifierWithErrFormatter().error({ errorType: 'Getting Journal Report', error });
                infoError.value = error
            }

            Message = `(Error) ${Message}: ${error instanceof AxiosError ? error : error.message}`;
            log.error(Message);
            Logger?.LogError(Message, Class);
        });
});
function ManagePdfDownloadUrlsAndButtonStatuses(data: IJournalReportItem[]) {
    log.trace("ManagePdfDownloadUrlsAndButtonStatuses()");

    /* Looping throuhgh table,
       instantiate backout button and pdf button statuses */
    data.forEach((item: IJournalReportItem) => {
        if (item.BackoutAvailable === true) {
            backoutButtonStatuses.value[item.JournalReference] = false;
            pdfButtonStatuses.value[item.ReportName] = false;
        }
    });
}
async function Backout(journalReportItem: IJournalReportItem) {
    const name = Backout.name;
    var Message = `Backed out Journal (Journal Reference: ${journalReportItem.JournalReference}, Job Id: ${jobId}, Journal type: ${journalType})`;
    const Class = fileName + "/" + name;

    // Disable button
    backoutButtonStatuses.value[journalReportItem.JournalReference] = true;

    // Reload table
    isLoading.value = true;

    log.debug(`name()\n${Message}`);
    // Send backout (POST) request
    const code: number = await jnlReportServices
        .PerformBackoutJournal(journalReportItem, jobId, journalType)
        .then((res: AxiosResponse<any>) => {
            return res.status;
        })
        .catch((error: any) => {
            if (error instanceof AxiosError) {
                useNotifierWithErrFormatter().error({ errorType: 'Backout Journal', error });
                infoError.value = error;
            }
            
            Message = `(Error) ${Message}: ${error instanceof AxiosError ? error : error.message}`;
            log.error(Message);
            Logger?.LogError(Message, Class);

            // Return a default value in case of error
            return -1;
        });

    log.debug("Backout()\nstatusCode", code);
    if (code === 201) {
        Logger?.LogInformation(Message, Class);

        await jnlReportServices
            .GetJournalingReport(jobId, journalType)
            .then((data: IJournalReportItem[]) => {
                log.debug("Backout()\nJournaling report data after backout:\n", data);
                journalingReport.value = data;
                displayedJournalingReport.value = journalingReport.value.map(
                    jnlReportServices.ParseItems
                );

                // Button Statuses
                ManagePdfDownloadUrlsAndButtonStatuses(data);
            })
            .catch((error: any) => {
                if (error instanceof AxiosError) {
                    useNotifierWithErrFormatter().error({ errorType: 'Getting Journal Report', error });
                    infoError.value = error;
                }

                Message = `(Error) ${Message}: ${error instanceof AxiosError ? error : error.message}`;
                log.error(Message);
                Logger?.LogError(Message, Class);
            });
    }
    isLoading.value = false;

    // Enable button
    backoutButtonStatuses.value[journalReportItem.JournalReference] = false;
}
// View full page pdf in new tab 
function viewFullPagePDF(fileID: string | undefined, fileType: string | undefined,) {
    log.trace("viewFullPagePDF()");
    log.debug(`"viewFullPagePDF()": fileID: ${fileID}, fileType: ${fileType}`);

    const rResolved = router.resolve({
        name: "Journaling Report (PDF)",
        params: {
            id: fileID,
        },
        query: {
            type: fileType,
            endpoint: process.env.VUE_APP_JOURNALING_REPORT_FILES_URL_PARAMS,
            method: "get",
        },
    });
    const url = rResolved.href;
    // Open in new tab
    window.open(url, "_blank");
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (isLoading.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, _hoisted_4))
      : (_openBlock(), _createElementBlock("div", _hoisted_5, [
          _hoisted_6,
          _createVNode(Table, {
            Rows: displayedJournalingReport.value,
            Cols: cols,
            Labels: labels,
            HasError: !!infoError.value
          }, {
            Status: _withCtx((props) => [
              (props.value.Status === 'SUCCESS')
                ? (_openBlock(), _createElementBlock("div", _hoisted_7, _toDisplayString(props.value.Status), 1))
                : _createCommentVNode("", true),
              (props.value.Status === 'FAILURE')
                ? (_openBlock(), _createElementBlock("div", _hoisted_8, _toDisplayString(props.value.Status), 1))
                : _createCommentVNode("", true)
            ]),
            BackoutAvailable: _withCtx((props) => [
              (props.value.BackoutAvailable)
                ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
                    _createElementVNode("button", {
                      onClick: ($event: any) => (Backout(props.value)),
                      ref: props.value.JournalReference,
                      disabled: 
                                backoutButtonStatuses.value[props.value.JournalReference] ||
                                _unref(DenyFunctionLevelAccess)(_unref(FunctionAccessLevels).BackoutJNLReport)
                            ,
                      title: _unref(DenyFunctionLevelAccess)(_unref(FunctionAccessLevels).BackoutJNLReport) ? `Backout ''${props.value.JournalReference}'', ${_unref(jobId)} (Access denied)` : `Backout ''${props.value.JournalReference}'' (${_unref(jobId)})`,
                      class: "table-button"
                    }, " Backout ", 8, _hoisted_10)
                  ]))
                : (_openBlock(), _createElementBlock("div", _hoisted_11))
            ]),
            ReportName: _withCtx((props) => [
              _createElementVNode("button", {
                onClick: ($event: any) => (viewFullPagePDF(props.value.ReportName,`Journaling`)),
                disabled: pdfButtonStatuses.value[props.value.ReportName],
                title: 
                            pdfButtonStatuses.value[props.value.ReportName]
                                ? ''
                                : 'View report pdf (opens file in new tab)'
                        ,
                class: "fake-link"
              }, _toDisplayString(props.value.ReportName), 9, _hoisted_12)
            ]),
            _: 1
          }, 8, ["Rows", "HasError"])
        ]))
  ]))
}
}

})